"use client";

import { useCallback, useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";

const STORAGE_KEY = "pickcel_utm_params";
const ATTRIBUTION_STORAGE_KEY = "pickcel_attribution_data";
const PREVIOUS_PAGE_KEY = "pickcel_previous_page_url";
const PERSIST_DAYS = 30;
const PERSIST_TTL_MS = PERSIST_DAYS * 24 * 60 * 60 * 1000;

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

// Click ID parameters from ad platforms
const CLICK_ID_PARAMS = [
  "gclid",    // Google Ads
  "gbraid",   // Google Ads (iOS 14.5+)
  "wbraid",   // Google Ads (web-to-app)
] as const;

export type UTMKey = (typeof UTM_PARAMS)[number];
export type ClickIDKey = (typeof CLICK_ID_PARAMS)[number];
export type UTMParams = Partial<Record<UTMKey, string>>;
export type ClickIDParams = Partial<Record<ClickIDKey, string>>;

export interface AttributionData {
  landing_page_url?: string;
  http_referrer?: string;
  previous_page_url?: string;
  click_ids?: ClickIDParams;
  oppref?: string;
}

type Persisted<T> = {
  data: T;
  expires_at: string;
};

const isExpired = (expiresAt?: string) => {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() <= Date.now();
};

const getStoredParams = (): UTMParams => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const persisted = JSON.parse(raw) as Persisted<UTMParams>;
    if (!persisted?.data || isExpired(persisted.expires_at)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return persisted.data;
  } catch (error) {
    console.warn("Failed to read UTM params from localStorage:", error);
    return {};
  }
};

const setStoredParams = (params: UTMParams) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const payload: Persisted<UTMParams> = {
      data: params,
      expires_at: new Date(Date.now() + PERSIST_TTL_MS).toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to persist UTM params in localStorage:", error);
  }
};

/**
 * Reads stored attribution data (including `oppref`) outside a React component
 * or context — used by components that render outside `UTMProvider`, such as
 * `ChatGptAdsCalendlyListener`.
 */
export const readStoredAttributionData = (): AttributionData => getStoredAttributionData();

const getStoredAttributionData = (): AttributionData => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const persisted = JSON.parse(raw) as Persisted<AttributionData>;
    if (!persisted?.data || isExpired(persisted.expires_at)) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }
    return persisted.data;
  } catch (error) {
    console.warn("Failed to read attribution data from localStorage:", error);
    return {};
  }
};

const setStoredAttributionData = (data: AttributionData) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const payload: Persisted<AttributionData> = {
      data,
      expires_at: new Date(Date.now() + PERSIST_TTL_MS).toISOString(),
    };
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to persist attribution data in localStorage:", error);
  }
};

const getStoredPreviousPageUrl = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    return window.sessionStorage.getItem(PREVIOUS_PAGE_KEY) || undefined;
  } catch (error) {
    console.warn("Failed to read previous page URL from sessionStorage:", error);
    return undefined;
  }
};

const setStoredPreviousPageUrl = (url?: string) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (!url) {
      window.sessionStorage.removeItem(PREVIOUS_PAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(PREVIOUS_PAGE_KEY, url);
  } catch (error) {
    console.warn("Failed to persist previous page URL in sessionStorage:", error);
  }
};

const extractFromSearchParams = (searchParams: URLSearchParams): UTMParams => {
  const extracted: UTMParams = {};

  UTM_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      extracted[key] = value;
    }
  });

  return extracted;
};

const extractClickIDs = (searchParams: URLSearchParams): ClickIDParams => {
  const extracted: ClickIDParams = {};

  CLICK_ID_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      extracted[key] = value;
    }
  });

  return extracted;
};

/** ChatGPT Ads' click-id equivalent. Passed through verbatim — never parsed or modified. */
export const extractOppref = (searchParams: URLSearchParams): string | undefined => {
  const value = searchParams.get("oppref");
  return value || undefined;
};

const stripTrackingParams = (rawUrl?: string) => {
  if (!rawUrl) return undefined;
  try {
    const url = new URL(rawUrl);
    UTM_PARAMS.forEach((key) => {
      url.searchParams.delete(key);
    });
    return url.toString();
  } catch (error) {
    console.warn("Failed to strip tracking params from URL:", error);
    return rawUrl;
  }
};

export function useUTM() {
  const pathname = usePathname();
  const [utmParams, setUtmParams] = useState<UTMParams>(() => getStoredParams());
  const [attributionData, setAttributionData] = useState<AttributionData>(() => getStoredAttributionData());
  const previousPathnameRef = useRef<string | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  // Track page navigation to store previous URL (session only)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentUrl = window.location.href;
    const currentPath = pathname;

    // If pathname changed, store the previous URL
    if (previousPathnameRef.current !== null && previousPathnameRef.current !== currentPath) {
      const previousUrl = stripTrackingParams(currentUrlRef.current || undefined);
      setStoredPreviousPageUrl(previousUrl);
      setAttributionData((prev) => ({
        ...prev,
        previous_page_url: previousUrl,
      }));
    }

    // Update refs for next navigation
    previousPathnameRef.current = currentPath;
    currentUrlRef.current = currentUrl;
  }, [pathname]);

  useEffect(() => {
    // Read the query in the browser: useSearchParams() forces the whole page to client-render.
    const searchParams = new URLSearchParams(window.location.search);
    const extracted = extractFromSearchParams(searchParams);
    const clickIDs = extractClickIDs(searchParams);
    const oppref = extractOppref(searchParams);
    const isDev = process.env.NODE_ENV !== "production";

    if (Object.keys(extracted).length > 0 || Object.keys(clickIDs).length > 0 || oppref) {
      // Last-touch attribution: overwrite with latest UTM params and click IDs.
      setStoredParams(extracted);
      setUtmParams(extracted);
      
      // Capture attribution data on first visit or when new UTMs arrive
      const existingAttribution = getStoredAttributionData();
      const previousPageUrl = getStoredPreviousPageUrl();
      const currentUrl = typeof window !== "undefined" ? window.location.href : undefined;
      const docReferrer = typeof window !== "undefined" && document.referrer ? document.referrer : undefined;
      
      const newAttributionData: AttributionData = {
        // Update landing_page_url on each new session with UTMs/click IDs (last-touch)
        landing_page_url: stripTrackingParams(currentUrl),
        // Use document.referrer if available, otherwise use stored previous_page_url
        http_referrer: stripTrackingParams(docReferrer) || undefined,
        previous_page_url: stripTrackingParams(previousPageUrl),
        click_ids: Object.keys(clickIDs).length > 0 ? clickIDs : existingAttribution.click_ids,
        oppref: oppref || existingAttribution.oppref,
      };

      setStoredAttributionData(newAttributionData);
      setAttributionData(newAttributionData);

      if (isDev) {
        console.log("[UTM] Stored from URL", extracted);
        if (Object.keys(clickIDs).length > 0) {
          console.log("[UTM] Click IDs captured", clickIDs);
        }
        if (oppref) {
          console.log("[UTM] oppref captured", oppref);
        }
        console.log("[UTM] Attribution data", newAttributionData);
      }
      return;
    }

    // Preserve stored UTMs during internal navigation with no UTM params.
    const stored = getStoredParams();
    const storedAttribution = getStoredAttributionData();
    const previousPageUrl = getStoredPreviousPageUrl();
    const currentUrl = typeof window !== "undefined" ? window.location.href : undefined;

    const updatedAttribution: AttributionData = {
      ...storedAttribution,
      // Ensure first-visited and last-visited are always available
      landing_page_url:
        stripTrackingParams(storedAttribution.landing_page_url) ||
        stripTrackingParams(currentUrl),
      http_referrer: stripTrackingParams(storedAttribution.http_referrer) || undefined,
      previous_page_url: stripTrackingParams(previousPageUrl),
    };
    
    setUtmParams(stored);
    setStoredAttributionData(updatedAttribution);
    setAttributionData(updatedAttribution);
    if (isDev && Object.keys(stored).length > 0) {
      console.log("[UTM] Loaded from localStorage", stored);
    }
  }, [pathname]);

  const getUTMParams = useCallback(() => ({ ...utmParams }), [utmParams]);
  
  const getAttributionData = useCallback(() => ({ ...attributionData }), [attributionData]);
  
  const getAllTrackingData = useCallback(() => {
    // Map to Zoho field names exactly as used in widget backend
    const zohoFields: Record<string, string> = {};
    
    // UTM parameters - using exact Zoho field names
    if (utmParams.utm_source) zohoFields.utm_source = utmParams.utm_source;
    if (utmParams.utm_medium) zohoFields.utm_medium = utmParams.utm_medium;
    if (utmParams.utm_campaign) zohoFields.utm_campaign = utmParams.utm_campaign;
    if (utmParams.utm_content) zohoFields.utm_content = utmParams.utm_content;
    if (utmParams.utm_term) zohoFields.utm_term = utmParams.utm_term;

    // Attribution fields - using exact Zoho field names
    if (attributionData.landing_page_url) {
      const landingUrl = stripTrackingParams(attributionData.landing_page_url);
      if (landingUrl) zohoFields.utm_first_visited_page = landingUrl;
    }
    if (attributionData.previous_page_url) {
      const prevUrl = stripTrackingParams(attributionData.previous_page_url);
      if (prevUrl) zohoFields.utm_last_visited_page = prevUrl;
    }
    // Conversion page
    const conversionPage = typeof window !== "undefined" ? window.location.href : undefined;
    const conversionUrl = stripTrackingParams(conversionPage);
    if (conversionUrl) zohoFields.utm_conversion_page = conversionUrl;
    
    return {
      utm: zohoFields,
    };
  }, [utmParams, attributionData]);

  return {
    utmParams,
    attributionData,
    getUTMParams,
    getAttributionData,
    getAllTrackingData,
  };
}
